import { useState } from "react";
import { Check, ChevronDown, Plus } from "lucide-react";
import { ADDONS, GROUPS, usd } from "@/lib/addons";

const AddonCard = ({ a, on, onToggle, compact }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={on}
    onClick={() => onToggle(a.id)}
    data-testid={`addon-${a.id}`}
    className={`group w-full text-left rounded-md border transition-[border-color,background-color] duration-200 ${compact ? "px-4 py-3" : "p-4 sm:p-5"} ${
      on ? "border-forest bg-mint" : "border-divider bg-white hover:border-sec/70"
    }`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-medium text-forest text-base sm:text-[15px] leading-snug">{a.name}</p>
        {a.blurb && <p className="mt-1 text-sm leading-relaxed text-sec line-clamp-2">{a.blurb}</p>}
      </div>
      <div className="shrink-0 flex flex-col items-end gap-2">
        <span className="font-mono text-sm text-ink">+{usd(a.price)}</span>
        <span
          className={`inline-flex h-8 min-w-[72px] items-center justify-center gap-1 rounded-full border text-xs font-medium transition-colors ${
            on ? "border-forest bg-forest text-white" : "border-sec/60 text-forest group-hover:border-forest"
          }`}
          data-testid={`addon-state-${a.id}`}
        >
          {on ? <><Check size={13} strokeWidth={3} className="animate-in zoom-in-50 duration-200" /> Added</> : <><Plus size={13} /> Add</>}
        </span>
      </div>
    </div>
  </button>
);

const Group = ({ name, items, selected, onToggle }) => {
  const [open, setOpen] = useState(false);
  const count = items.filter((a) => selected.includes(a.id)).length;
  return (
    <div className="border-t border-divider">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex w-full items-center justify-between py-4 text-left min-h-[48px]" data-testid={`addon-group-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
        <span className="font-medium text-forest">{name} <span className="mono text-sec ml-2">{items.length} tests{count ? ` · ${count} added` : ""}</span></span>
        <ChevronDown size={16} className={`text-forest transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-5 grid gap-2 reveal">{items.map((a) => <AddonCard key={a.id} a={a} on={selected.includes(a.id)} onToggle={onToggle} compact />)}</div>}
    </div>
  );
};

export const StepAddons = ({ data, toggleAddon, note }) => {
  const [advanced, setAdvanced] = useState(false);
  const rec = ADDONS.filter((a) => a.group === "recommended");
  return (
    <div data-testid="step-addons">
      <h3 className="h-editorial !text-2xl sm:!text-3xl">Go deeper, only where it matters.</h3>
      <p className="mt-3 text-[15px] leading-relaxed text-sec max-w-xl">
        Your assessment already includes baseline water and 7-day air testing. These optional laboratory tests investigate specific concerns in greater depth.
      </p>

      <p className="mono uppercase tracking-[0.18em] text-sec mt-7 mb-3">Most popular</p>
      <div className="grid gap-2.5" data-testid="addons-recommended">
        {rec.map((a) => <AddonCard key={a.id} a={a} on={data.addons.includes(a.id)} onToggle={toggleAddon} />)}
      </div>

      <p className="mt-3 min-h-[1.25rem] text-sm text-forest" aria-live="polite" data-testid="addon-note">{note}</p>

      <div className="mt-6">
        <button type="button" onClick={() => setAdvanced((v) => !v)} aria-expanded={advanced} className="inline-flex items-center gap-1.5 text-sm font-medium text-forest underline-offset-4 hover:underline min-h-[44px]" data-testid="addons-explore">
          Explore advanced testing <ChevronDown size={16} className={`transition-transform ${advanced ? "rotate-180" : ""}`} />
        </button>
        {advanced && (
          <div className="mt-2 reveal" data-testid="addons-advanced">
            {GROUPS.map((g) => <Group key={g} name={g} items={ADDONS.filter((a) => a.group === g)} selected={data.addons} onToggle={toggleAddon} />)}
          </div>
        )}
        <p className="mt-4 border-t border-divider pt-4 text-sm text-sec leading-relaxed" data-testid="addons-reassurance">Not sure? Your technician can recommend additional testing after the initial assessment.</p>
      </div>
    </div>
  );
};
